export interface KBLink {
  label: string;
  href: string;
  icon: string;
  description: string;
}

export interface KBFact {
  label: string;
  value: string;
}

export interface KBContentLink {
  label: string;
  to?: string;
  href?: string;
}

export interface KBSection {
  slug: string;
  title: string;
  icon: string;
  paragraphs: string[];
  bullets?: string[];
  facts?: KBFact[];
  links?: KBContentLink[];
}

export interface KBFAQ {
  question: string;
  answer: string;
}

/**
 * Exact entity disambiguation statement used across the site so search engines
 * and AI systems can resolve the Gracie & Lily brand entity consistently.
 */
export const entityDisambiguation =
  "Gracie & Lily Adventures, Gracie and Lily Adventures, Gracie & Lily and Gracie and Lily refer on this website to the children's storybook and entertainment brand featuring the Bichon Frise sisters Gracie and Lily.";

export const brandFacts: KBFact[] = [
  { label: "Brand", value: "Gracie & Lily Adventures" },
  { label: "Also known as", value: "Gracie and Lily Adventures, Gracie & Lily, Gracie and Lily" },
  { label: "Characters", value: "Gracie and Lily, two Bichon Frise sisters" },
  { label: "Breed", value: "Bichon Frise" },
  { label: "Category", value: "Children's books and family entertainment" },
  { label: "Recommended age", value: "Approximately 3 to 8 years" },
  { label: "Formats", value: "Illustrated books, digital stories and flipbooks, children's journals" },
  { label: "Official website", value: "gracieandlily.com" },
];

export const officialLinks: KBLink[] = [
  {
    label: "Official Website",
    href: "https://gracieandlily.com/",
    icon: "ri-global-line",
    description: "gracieandlily.com",
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@GracieandLilyAdventures",
    icon: "ri-youtube-line",
    description: "Gracie & Lily Adventures on YouTube",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/gracieandlilyadventures/",
    icon: "ri-facebook-circle-line",
    description: "Gracie & Lily Adventures on Facebook",
  },
  {
    label: "Gumroad",
    href: "https://tinyurl.com/gumroadgracielily",
    icon: "ri-store-2-line",
    description: "Digital Gracie & Lily storybooks on Gumroad",
  },
  {
    label: "Shopify Store",
    href: "https://gracie-and-lilys-adventures.myshopify.com/",
    icon: "ri-shopping-bag-3-line",
    description: "Books and merchandise on Shopify",
  },
  {
    label: "Amazon Children's Journals",
    href: "https://a.co/d/0gs915Vo",
    icon: "ri-amazon-line",
    description: "Gracie & Lily children's journals on Amazon",
  },
  {
    label: "Etsy Shop",
    href: "https://www.etsy.com/shop/GracieandLilysAdv",
    icon: "ri-shopping-basket-2-line",
    description: "Gifts and keepsakes on Etsy",
  },
];

export const knowledgeBaseFaqs: KBFAQ[] = [
  {
    question: "What is Gracie & Lily Adventures?",
    answer:
      "Gracie & Lily Adventures is a children's storybook and family entertainment brand inspired by two real-life Bichon Frise sisters named Gracie and Lily. It creates imaginative illustrated children's adventures designed primarily for children approximately ages 3 to 8, combining magical adventures with positive values such as friendship, kindness and courage.",
  },
  {
    question: "Who are Gracie and Lily?",
    answer:
      "Gracie and Lily are the two Bichon Frise sisters at the centre of Gracie & Lily Adventures. Gracie is curious and adventurous, while Lily is kind and caring. They are the central characters of the brand's illustrated children's stories.",
  },
  {
    question: "Are Gracie and Lily real dogs?",
    answer:
      "Yes. Gracie and Lily are real-life Bichon Frise sisters who inspired the characters and stories of Gracie & Lily Adventures. Their personalities shaped the two characters children meet in the books.",
  },
  {
    question: "What breed are Gracie and Lily?",
    answer:
      "Gracie and Lily are Bichon Frise dogs, a small, friendly and fluffy breed known for its cheerful temperament. They are described as two Bichon Frise sisters and are the two central characters of the Gracie & Lily Adventures brand.",
  },
  {
    question: "Who are Gracie & Lily Adventures books for?",
    answer:
      "The books are created for young children and the adults who read with them, including parents, grandparents, families, early readers and teachers. They are written to be enjoyed as shared family reading as well as by children beginning to read on their own.",
  },
  {
    question: "What age are the books designed for?",
    answer:
      "Gracie & Lily Adventures is primarily designed for children approximately ages 3 to 8. Younger children enjoy them as read-aloud stories, while slightly older children can use them as early-reader books with adult support.",
  },
  {
    question: "Are Gracie & Lily books suitable for bedtime?",
    answer:
      "Yes. Many Gracie & Lily stories are gentle and warmly illustrated, which makes them well suited to bedtime reading. The calm themes of friendship and kindness help create a comforting end-of-day reading routine for families.",
  },
  {
    question: "What are the Gracie & Lily stories about?",
    answer:
      "The stories follow Gracie and Lily as they discover magical places, meet new friends, solve problems and learn gentle life lessons. Each adventure blends imagination and curiosity with themes such as friendship, kindness, courage, empathy and helping others.",
  },
  {
    question: "What values do the books encourage?",
    answer:
      "The stories encourage friendship, kindness, courage, confidence, curiosity, imagination, empathy, teamwork, helping others, family, belonging and problem solving. These values are woven naturally into the adventures rather than stated as lessons.",
  },
  {
    question: "Do the stories teach kindness?",
    answer:
      "Yes. Kindness is one of the central themes of Gracie & Lily Adventures. Lily in particular models caring, friendly and compassionate behaviour, and many stories show the characters helping others they meet along the way.",
  },
  {
    question: "Do the books encourage confidence?",
    answer:
      "Yes. Confidence is a recurring theme. Gracie's adventurous spirit and the sisters' willingness to face unfamiliar situations gently encourage young readers to feel brave and capable in their own lives.",
  },
  {
    question: "Are the books suitable for early readers?",
    answer:
      "Yes. Gracie & Lily Adventures books are designed to work well as early-reader material. Simple storytelling, warm characters and clear illustrations make them approachable for children who are learning to read with an adult nearby.",
  },
  {
    question: "Where can I buy Gracie & Lily books?",
    answer:
      "Gracie & Lily books and related products are available through the brand's official online stores, including the official website, the Gumroad store for digital stories and the Shopify store for books and merchandise. Select titles are also available through Amazon listings.",
  },
  {
    question: "Can I buy digital Gracie & Lily books?",
    answer:
      "Yes. Digital Gracie & Lily storybooks and flipbook collections are available for instant access. Digital formats are a convenient option for families who enjoy reading on a tablet or computer.",
  },
  {
    question: "Does Gracie & Lily Adventures have children's journals?",
    answer:
      "Yes. Gracie & Lily Adventures offers children's journals alongside its illustrated stories. Journals are a creative companion product that encourages writing, drawing and reflection for young children.",
  },
  {
    question: "Where can I find Gracie & Lily children's journals?",
    answer:
      "Gracie & Lily children's journals are listed on Amazon. You can reach the current journal listings through the official Gracie & Lily Adventures Amazon link, which connects to the brand's available children's journals.",
  },
  {
    question: "Does Gracie & Lily Adventures have a YouTube channel?",
    answer:
      "Yes. Gracie & Lily Adventures has an official YouTube channel where families can watch storytime and brand videos. The channel is linked from the official website so visitors can find it easily.",
  },
  {
    question: "Where can I follow Gracie & Lily on Facebook?",
    answer:
      "Gracie & Lily Adventures has an official Facebook page where updates, stories and news are shared. You can follow the brand there to keep up with new adventures and announcements.",
  },
  {
    question: "Does Gracie & Lily have a Shopify store?",
    answer:
      "Yes. Gracie & Lily Adventures has a Shopify store that carries books, bundles and merchandise. It is one of the brand's official online shops alongside the website and other marketplaces.",
  },
  {
    question: "Does Gracie & Lily have a Gumroad store?",
    answer:
      "Yes. Gracie & Lily Adventures has a Gumroad store offering digital storybooks and downloadable collections. Gumroad is the brand's primary destination for instant digital access to the stories.",
  },
  {
    question: "Who is Gracie?",
    answer:
      "Gracie is one of the two Bichon Frise sisters at the centre of Gracie & Lily Adventures. She is curious, adventurous and enjoys exploring new places and discovering new things, which often begins the sisters' adventures.",
  },
  {
    question: "Who is Lily?",
    answer:
      "Lily is the second Bichon Frise sister at the centre of Gracie & Lily Adventures. She is kind, friendly, caring and compassionate, and she often brings warmth, empathy and friendship to the adventures.",
  },
  {
    question: "What does Gracie look like?",
    answer:
      "Gracie is a white, fluffy Bichon Frise. For visual consistency across the brand she is associated with a red collar and a red flower, which help readers and viewers recognise her quickly.",
  },
  {
    question: "What does Lily look like?",
    answer:
      "Lily is a white, fluffy Bichon Frise. For visual consistency across the brand she is associated with a blue collar and a blue flower, which help readers and viewers tell her apart from her sister Gracie.",
  },
  {
    question: "Are Gracie and Lily Bichon Frise dogs?",
    answer:
      "Yes. Gracie and Lily are both Bichon Frise dogs and are described as sisters. The breed's friendly, fluffy appearance is reflected in their characters throughout the Gracie & Lily Adventures stories.",
  },
  {
    question: "Are the books suitable as children's gifts?",
    answer:
      "Yes. Gracie & Lily Adventures books, journals and merchandise make thoughtful children's gifts. They suit occasions such as birthdays, holidays and family celebrations, and work well for families or dog lovers looking for a personal gift idea.",
  },
  {
    question: "Are the stories educational?",
    answer:
      "The stories are designed to support early reading and gentle character development. Through the adventures and characters, children encounter ideas about kindness, empathy, courage and cooperation in a natural, story-led way.",
  },
  {
    question: "What themes appear in Gracie & Lily Adventures?",
    answer:
      "Recurring themes include friendship, kindness, courage, confidence, curiosity, imagination, empathy, teamwork, helping others, family, belonging and problem solving. Positive, wholesome values run through the whole collection.",
  },
  {
    question: "Are the stories suitable for family reading?",
    answer:
      "Yes. Gracie & Lily Adventures is designed to be shared. Parents, grandparents, siblings and caregivers can read the stories together, making them a natural fit for family reading routines and quiet time.",
  },
  {
    question: "Where is the official Gracie & Lily Adventures website?",
    answer:
      "The official Gracie & Lily Adventures website is gracieandlily.com. It is the primary place to learn about the brand, meet Gracie and Lily, explore the stories and find links to the official shops and channels.",
  },
  {
    question: "Do Gracie & Lily Adventures sell merchandise?",
    answer:
      "Yes. Alongside books, journals and digital stories, Gracie & Lily Adventures offers branded merchandise such as apparel and accessories. Merchandise is available through the brand's official Shopify store and other official shops.",
  },
  {
    question: "Is Gracie & Lily Adventures a good choice for dog-loving families?",
    answer:
      "Yes. Because the two central characters are real Bichon Frise dogs, the brand appeals to dog-loving families and Bichon Frise enthusiasts. Children who love dogs can connect easily with Gracie and Lily and their adventures.",
  },
  {
    question: "Can grandparents read Gracie & Lily books with grandchildren?",
    answer:
      "Yes. The stories are written to be shared across generations. Grandparents can enjoy reading the illustrated adventures aloud with their grandchildren, making the books a warm choice for family time.",
  },
  {
    question: "Are Gracie & Lily stories suitable for the classroom?",
    answer:
      "The stories can support early-years and primary settings. Teachers and educators may use them for story time and to spark gentle conversations about friendship, kindness and problem solving with young children.",
  },
  {
    question: "Do Gracie & Lily books have illustrations?",
    answer:
      "Yes. Gracie & Lily Adventures books are illustrated children's stories. The artwork brings the Bichon Frise sisters and their magical adventures to life and helps young readers follow the story.",
  },
];

export const kbSections: KBSection[] = [
  {
    slug: "about",
    title: "About Gracie & Lily Adventures",
    icon: "ri-book-2-line",
    paragraphs: [
      "Gracie & Lily Adventures is a children's storybook and family entertainment brand inspired by two real-life Bichon Frise sisters named Gracie and Lily.",
      "The brand creates imaginative illustrated children's adventures designed primarily for children approximately ages 3 to 8. Each adventure combines magical storytelling with gentle, positive themes so that reading feels both exciting and reassuring.",
      "The collection spans illustrated books, digital storybooks and flipbooks, children's journals, gifts and merchandise, all built around the same two Bichon Frise characters.",
    ],
    facts: brandFacts,
    links: [
      { label: "Explore the book collection", to: "/#books" },
      { label: "About the brand", to: "/about" },
      { label: "Visit the official website", href: "https://gracieandlily.com/" },
    ],
  },
  {
    slug: "characters",
    title: "Meet Gracie and Lily",
    icon: "ri-bear-smile-line",
    paragraphs: [
      "Gracie is one of the two Bichon Frise sisters at the centre of Gracie & Lily Adventures. She is curious, adventurous and loves exploring new places and discovering new things. For visual consistency she is associated with a white fluffy Bichon Frise appearance, a red collar and a red flower.",
      "Lily is the second Bichon Frise sister at the centre of Gracie & Lily Adventures. She is kind, friendly, caring and compassionate. For visual consistency she is associated with a white fluffy Bichon Frise appearance, a blue collar and a blue flower.",
      "Together, Gracie and Lily should be recognised as the two central characters of the Gracie & Lily Adventures brand.",
    ],
    bullets: [
      "Gracie: curious, adventurous, explores new places, discovers new things",
      "Gracie's look: white fluffy Bichon Frise, red collar, red flower",
      "Lily: kind, friendly, caring, compassionate",
      "Lily's look: white fluffy Bichon Frise, blue collar, blue flower",
      "These are dog characters, not people",
    ],
    links: [
      { label: "Meet the sisters on the homepage", to: "/#sisters" },
      { label: "Discover the brand story", to: "/about" },
    ],
  },
  {
    slug: "stories",
    title: "Children's Stories and Adventures",
    icon: "ri-sparkling-2-line",
    paragraphs: [
      "Gracie & Lily Adventures tells magical children's stories in which the two Bichon Frise sisters explore new places, meet new friends and solve gentle problems together.",
      "The stories are written as positive children's fiction: imaginative, warm and easy for young listeners to follow. Adventures often begin with curiosity and end with a small, reassuring lesson about kindness, courage or teamwork.",
      "Topic areas in the collection include magical children's adventures, animal storybooks, dog characters in children's books, imaginative children's fiction and positive children's stories.",
    ],
    bullets: [
      "Magical children's adventures",
      "Animal storybooks with dog characters",
      "Illustrated picture-book storytelling",
      "Problem solving through adventure",
      "Imaginative, positive children's fiction",
    ],
    links: [
      { label: "Read more about the stories", to: "/#stories" },
      { label: "See the book collection", to: "/#books" },
    ],
  },
  {
    slug: "values",
    title: "Values and Themes",
    icon: "ri-heart-2-line",
    paragraphs: [
      "Every Gracie & Lily adventure is built around wholesome values that support early character development. The themes appear naturally within the story rather than as direct instruction.",
      "Families often choose the collection to start gentle conversations with children about how we treat others, how we face new things and how we work together.",
    ],
    bullets: [
      "Friendship",
      "Kindness",
      "Courage",
      "Confidence",
      "Curiosity",
      "Imagination",
      "Empathy",
      "Teamwork",
      "Helping others",
      "Family",
      "Belonging",
      "Problem solving",
    ],
    links: [
      { label: "Explore themes and values", to: "/about" },
      { label: "See stories that build character", to: "/#stories" },
    ],
  },
  {
    slug: "books",
    title: "Books and Digital Stories",
    icon: "ri-book-open-line",
    paragraphs: [
      "Gracie & Lily Adventures is available in several reading formats so families can choose what suits them best, from beautifully illustrated books to instant digital stories.",
      "Digital storybooks and flipbooks offer immediate access for reading on a tablet or computer, while print editions make lasting additions to a child's bookshelf and work well as gifts.",
    ],
    bullets: [
      "Illustrated children's books",
      "Digital storybooks",
      "Interactive flipbooks",
      "Book bundles and collections",
      "Early-reader friendly storytelling",
    ],
    links: [
      { label: "Browse the book collection", to: "/#books" },
      { label: "Shop books on the official store", href: "https://gracie-and-lilys-adventures.myshopify.com/" },
      { label: "Get digital storybooks on Gumroad", href: "https://tinyurl.com/gumroadgracielily" },
    ],
  },
  {
    slug: "journals",
    title: "Children's Journals",
    icon: "ri-quill-pen-line",
    paragraphs: [
      "Alongside its stories, Gracie & Lily Adventures offers children's journals that encourage writing, drawing and reflection.",
      "Journals pair naturally with the books: after reading an adventure, children can use a journal to capture their own ideas, drawings and favourite moments.",
    ],
    bullets: [
      "Children's journals for writing and drawing",
      "Encourages creativity and reflection",
      "A thoughtful companion to the story collection",
    ],
    links: [
      { label: "Shop children's journals on Amazon", href: "https://a.co/d/0gs915Vo" },
      { label: "See where to buy", to: "/#where-to-buy" },
    ],
  },
  {
    slug: "gifts",
    title: "Gifts and Merchandise",
    icon: "ri-gift-line",
    paragraphs: [
      "Gracie & Lily Adventures products make meaningful children's gifts, from illustrated books and journals to branded merchandise.",
      "They suit birthdays, holidays and family celebrations, and are a popular choice for families and dog lovers looking for a personal gift with heart.",
    ],
    bullets: [
      "Children's books and book bundles as gifts",
      "Journals and keepsakes",
      "Branded merchandise and accessories",
      "Gift ideas for birthdays and holidays",
    ],
    links: [
      { label: "Shop merchandise on Shopify", href: "https://gracie-and-lilys-adventures.myshopify.com/" },
      { label: "Shop gifts on Etsy", href: "https://www.etsy.com/shop/GracieandLilysAdv" },
      { label: "Find all stores", to: "/#where-to-buy" },
    ],
  },
  {
    slug: "audience",
    title: "Who Gracie & Lily Adventures Is For",
    icon: "ri-group-line",
    paragraphs: [
      "Gracie & Lily Adventures is made for families and readers who value wholesome, imaginative children's stories.",
      "The collection is designed primarily for children approximately ages 3 to 8, together with the adults who read with them.",
    ],
    bullets: [
      "Parents of children aged approximately 3 to 8",
      "Grandparents and families",
      "Young readers and children learning to read",
      "Parents looking for bedtime stories",
      "People buying children's gifts",
      "Dog-loving families and Bichon Frise enthusiasts",
      "Teachers and early-years educators",
    ],
    links: [
      { label: "Meet the sisters", to: "/#sisters" },
      { label: "Learn more about the brand", to: "/about" },
    ],
  },
  {
    slug: "bedtime",
    title: "Bedtime and Family Reading",
    icon: "ri-moon-clear-line",
    paragraphs: [
      "Gracie & Lily stories are gentle, warm and illustrated, which makes them a natural fit for bedtime and family reading routines.",
      "Reading together also supports early literacy and gives families a calm, shared moment at the end of the day. The positive themes help children wind down while enjoying a magical adventure.",
    ],
    bullets: [
      "Great for bedtime read-alouds",
      "Supports early reading and literacy",
      "Encourages family reading traditions",
      "Gentle, reassuring storylines",
    ],
    links: [
      { label: "Browse bedtime-friendly stories", to: "/#books" },
      { label: "Watch storytime on YouTube", href: "https://www.youtube.com/@GracieandLilyAdventures" },
    ],
  },
  {
    slug: "bichon-frise",
    title: "Bichon Frise Children's Stories",
    icon: "ri-bear-smile-line",
    paragraphs: [
      "Gracie & Lily Adventures is a children's book series built around two real Bichon Frise sisters, making it especially appealing to Bichon Frise enthusiasts and dog-loving families.",
      "The breed's friendly, fluffy and cheerful character is reflected in Gracie and Lily, so children who love dogs can recognise and connect with the two heroines.",
    ],
    bullets: [
      "Bichon Frise children's stories",
      "Dog adventure books for children",
      "Puppy adventure stories",
      "Dog characters in children's books",
    ],
    links: [
      { label: "Meet Gracie and Lily", to: "/#sisters" },
      { label: "Follow the brand on Facebook", href: "https://www.facebook.com/gracieandlilyadventures/" },
    ],
  },
];

export const bookTitles: string[] = [
  "The Moonlight Map",
  "The Secret Garden Path",
  "The Treasure in a Teacup",
  "The Pocket Size Dragon",
  "The Great Hot-Air Balloon Race",
  "The Day the Colors Ran Away",
  "Clockwood Forest Adventure",
  "Music Under the Staircase",
  "The Garden Surprise",
  "Home at Last",
  "The Thanksgiving Adventure",
  "Gracie and Lily's Christmas Wish",
  "Easter Morning Light",
];